import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Calendar, MessageSquare, FileText, TrendingUp } from 'lucide-react';
import { getStats } from '../../services/adminApi';
import { toast } from 'sonner';
import { Toaster } from '../../components/ui/sonner';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const data = await getStats();
      setStats(data);
    } catch (error) {
      toast.error('İstatistikler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600">Yükleniyor...</div>
        </div>
      </AdminLayout>
    );
  }

  const statCards = [
    {
      title: 'Toplam Randevu',
      value: stats?.appointments?.total || 0,
      icon: Calendar,
      color: 'emerald',
      bg: 'bg-emerald-50',
      text: 'text-emerald-700'
    },
    {
      title: 'Bekleyen Randevu',
      value: stats?.appointments?.pending || 0,
      icon: TrendingUp,
      color: 'amber',
      bg: 'bg-amber-50',
      text: 'text-amber-700'
    },
    {
      title: 'Okunmamış Mesaj',
      value: stats?.messages?.unread || 0,
      icon: MessageSquare,
      color: 'blue',
      bg: 'bg-blue-50',
      text: 'text-blue-700'
    },
    {
      title: 'Blog Yazıları',
      value: stats?.blog_posts || 0,
      icon: FileText,
      color: 'purple',
      bg: 'bg-purple-50',
      text: 'text-purple-700'
    },
  ];

  return (
    <AdminLayout>
      <Toaster position="top-center" />
      
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
          <p className="text-gray-600">Uzman Veteriner Kliniği yönetim paneline hoş geldiniz</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <div
                key={index}
                className="bg-white rounded-xl shadow-md p-6 border border-gray-200 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`${card.bg} p-3 rounded-lg`}>
                    <Icon className={`h-6 w-6 ${card.text}`} />
                  </div>
                </div>
                <h3 className="text-gray-600 text-sm font-medium mb-1">{card.title}</h3>
                <p className="text-3xl font-bold text-gray-900">{card.value}</p>
              </div>
            );
          })}
        </div>

        {/* Quick Stats */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Appointments Overview */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Randevu Durumu</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Beklemede</span>
                <span className="font-semibold text-amber-700">{stats?.appointments?.pending || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Onaylandı</span>
                <span className="font-semibold text-emerald-700">{stats?.appointments?.confirmed || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Toplam</span>
                <span className="font-semibold text-gray-900">{stats?.appointments?.total || 0}</span>
              </div>
            </div>
          </div>

          {/* Messages Overview */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Mesaj Durumu</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Okunmamış</span>
                <span className="font-semibold text-blue-700">{stats?.messages?.unread || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Toplam</span>
                <span className="font-semibold text-gray-900">{stats?.messages?.total || 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-gradient-to-r from-emerald-700 to-amber-700 rounded-xl shadow-lg p-8 text-white">
          <h3 className="text-2xl font-bold mb-2">Hızlı İşlemler</h3>
          <p className="text-emerald-50 mb-6">
            Yönetim panelinden randevuları, mesajları ve blog yazılarını kolayca yönetin
          </p>
          <div className="flex flex-wrap gap-4">
            <a
              href="/admin/randevular"
              className="bg-white text-emerald-800 px-6 py-3 rounded-lg font-semibold hover:bg-emerald-50 transition-colors"
            >
              Randevuları Görüntüle
            </a>
            <a
              href="/admin/mesajlar"
              className="bg-white/10 backdrop-blur-sm text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/20 transition-colors border border-white/20"
            >
              Mesajları Görüntüle
            </a>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
